/**
 * Admin Application Router
 * Handles all admin panel routing
 */

import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SupabaseAdminLogin } from "./components/SupabaseAdminLogin";
import { TenantControlCenter } from "./components/TenantControlCenter";
import { CouponManagement } from "./components/CouponManagement";
import { PaymentProviderManagement } from "./components/PaymentProviderManagement";
import { supabase } from "@/integrations/supabase/client";

import { AdminSidebarLayout } from "./layout/AdminSidebarLayout";
import { AdminDashboardIndex } from "./pages/AdminDashboardIndex";
import { AnalyticsDashboard } from "./pages/AnalyticsDashboard";
import { AuditLogs } from "./pages/AuditLogs";
import { AdminSettings } from "./pages/AdminSettings";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 5 * 60 * 1000 },
  },
});

type CachedAdminVerification = {
  verified: boolean;
  checkedAt: number;
  userId: string;
};

// Memory cache to prevent redundant RPC checks on quick sub-route navigations, scoped per user
let cachedAdminVerification: CachedAdminVerification | null = null;
const CACHE_TTL_MS = 30 * 1000; // 30 seconds TTL for near-instant revocation enforcement

/**
 * Check if the current user has a valid admin session.
 */
async function hasValidAdminSession(): Promise<boolean> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      localStorage.removeItem('admin_ui_state');
      cachedAdminVerification = null;
      return false;
    }

    const currentUserId = session.user.id;
    const now = Date.now();

    // Check memory cache ensuring it matches the active user and is within TTL
    if (
      cachedAdminVerification &&
      cachedAdminVerification.userId === currentUserId &&
      (now - cachedAdminVerification.checkedAt < CACHE_TTL_MS)
    ) {
      return cachedAdminVerification.verified;
    }

    // Always fetch and verify role with the backend
    const { data: role, error } = await supabase.rpc('get_user_role_type');
    
    if (error) {
      console.error('System error occurred during verification.');
      // Transient system/network failure: do not clear user session immediately,
      // return false to block route rendering but allow subsequent retries
      return false;
    }

    if (role !== 'admin') {
      console.warn('Unauthorized access attempt blocked.');
      localStorage.removeItem('admin_ui_state');
      cachedAdminVerification = null;
      await supabase.auth.signOut();
      return false;
    }

    // Store verified verification state in memory cache tied to this specific user
    cachedAdminVerification = { 
      verified: true, 
      checkedAt: now,
      userId: currentUserId 
    };

    // Refresh non-sensitive UI-only cached state in localStorage (no access token stored here)
    const adminUIState = {
      user: {
        id: currentUserId,
        email: session.user.email,
        role: role,
        is_active: true,
      },
      expires_at: session.expires_at,
    };
    localStorage.setItem('admin_ui_state', JSON.stringify(adminUIState));
    return true;
  } catch (err) {
    console.error('Error occurred during verification.');
    localStorage.removeItem('admin_ui_state');
    cachedAdminVerification = null;
    return false;
  }
}

// Admin Protected Route Wrapper
const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading');

  useEffect(() => {
    let cancelled = false;
    hasValidAdminSession().then((ok) => {
      if (!cancelled) setStatus(ok ? 'authenticated' : 'unauthenticated');
    });
    return () => { cancelled = true; };
  }, []);

  // Listen to auth state changes to immediately invalidate state/cache on signout
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        localStorage.removeItem('admin_ui_state');
        cachedAdminVerification = null;
        setStatus('unauthenticated');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-xl overflow-hidden border-2 border-purple-500/50 relative animate-pulse">
            <img src="/images/avatars/ravan_ultimate.png" alt="Loading" className="w-full h-full object-cover" />
          </div>
          <p className="text-sm font-semibold tracking-widest uppercase text-muted-foreground">Verifying Admin Access...</p>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

const AdminApp = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Routes>
            {/* Admin Login Route - MUST COME FIRST */}
            <Route path="login" element={<SupabaseAdminLogin />} />
            
            {/* Shell Layout for all authenticated admin routes */}
            <Route element={<AdminProtectedRoute><AdminSidebarLayout /></AdminProtectedRoute>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboardIndex />} />
              <Route path="tenants" element={<TenantControlCenter />} />
              <Route path="coupons" element={<CouponManagement />} />
              <Route path="payments" element={<PaymentProviderManagement />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="audit" element={<AuditLogs />} />
            </Route>

            {/* Unmatched admin routes go to dashboard */}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default AdminApp;
